import { useEffect, useState } from "react";
import Sidebar from "@/components/layout/homepage/Sidebar";
import { useSidebar } from "@/contexts/SidebarContext";
import { cn } from "@/components/utils/utils";
import type { UserRole } from "@/lib/auth/models/auth.model";

interface MobileSidebarModalProps {
  isOpen: boolean;
  onClose: () => void;
  userRole: UserRole | null;
}

const MobileSidebarModal = ({ isOpen, onClose, userRole }: MobileSidebarModalProps) => {
  const [isAnimating, setIsAnimating] = useState(false);
  const [isVisible, setIsVisible] = useState(false);

  // Gestione apertura con animazione
  useEffect(() => {
    if (isOpen && !isVisible) {
      setIsVisible(true);
      setIsAnimating(true);
      // Delay per permettere l'animazione di apertura
      setTimeout(() => {
        setIsAnimating(false);
      }, 500);
    }
  }, [isOpen, isVisible]);

  const handleClose = () => {
    setIsAnimating(true);
    // Delay per permettere l'animazione di chiusura
    setTimeout(() => {
      onClose();
      setIsVisible(false);
      setIsAnimating(false);
    }, 500);
  };

  // Cleanup quando il componente viene smontato
  useEffect(() => {
    return () => {
      setIsVisible(false);
      setIsAnimating(false);
    };
  }, []);

  if (!isVisible) return null;

  return (
    <div className="fixed inset-0 z-[9999] md:hidden">
      {/* Backdrop */}
      <div 
        className={cn(
          "absolute inset-0 bg-black/60 backdrop-blur-sm transition-all duration-500 ease-out",
          isOpen && !isAnimating 
            ? "opacity-100" 
            : "opacity-0"
        )}
        onClick={handleClose}
      />
      
      {/* Modal Container - Click through to backdrop */}
      <div className="absolute inset-0 flex items-center justify-center p-4 pointer-events-none">
        {/* Modal Content - Re-enable pointer events */}
        <div 
          className={cn(
            "relative w-full max-w-sm h-[80vh] bg-white rounded-lg shadow-2xl transition-all duration-500 ease-out pointer-events-auto",
            // Animazione di apertura e chiusura: scale
            isOpen && !isAnimating
              ? "scale-100 opacity-100"
              : "scale-95 opacity-0"
          )}
          onClick={(e) => e.stopPropagation()}
        >
          {/* Close Button with fancy animation */}
          <button
            onClick={handleClose}
            className={cn(
              "absolute top-4 right-4 z-10 p-2 rounded-full bg-gray-100 hover:bg-gray-200 transition-all duration-300 ease-out",
              // Animazione del pulsante di chiusura
              isOpen && !isAnimating
                ? "scale-100 opacity-100"
                : "scale-0 opacity-0"
            )}
            style={{ transitionDelay: isOpen ? "200ms" : "0ms" }}
          >
            <svg className="w-5 h-5 text-gray-600" fill="none" stroke="currentColor" viewBox="0 0 24 24">
              <path strokeLinecap="round" strokeLinejoin="round" strokeWidth={2} d="M6 18L18 6M6 6l12 12" />
            </svg>
          </button>
          
          {/* Sidebar Content with staggered animation */}
          <div 
            className={cn(
              "h-full overflow-hidden rounded-lg transition-all duration-500 ease-out",
              // Animazione del contenuto
              isOpen && !isAnimating
                ? "opacity-100"
                : "opacity-0"
            )}
            style={{ transitionDelay: isOpen ? "100ms" : "0ms" }}
          >
            <Sidebar 
              isCollapsed={false} 
              onToggle={handleClose}
              userRole={userRole}
            />
          </div>
        </div>
      </div>
    </div>
  );
};

export default MobileSidebarModal;
